import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Section } from '@dized/ui';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { isValidStateData } from '../common/config/analyticsConfig';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement
);

const ResponsiveTitle = styled.h3`
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  margin: 0 0 20px 0;
  word-break: break-word;
  line-height: 1.3;
  
  @media (max-width: 480px) {
    margin: 0 0 15px 0;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  align-items: center;
`;

const ToggleButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: ${props => props.active ? '#2196F3' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#1976D2' : '#f5f5f5'};
  }
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  margin-top: 20px;
`;

const ChartWrapper = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const ChartContainer = styled.div`
  height: 400px;
  width: 100%;
  max-width: 100%;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    height: 350px;
  }

  @media (max-width: 480px) {
    height: 300px;
  }
`;

const ChartTitle = styled.h4`
  margin: 0 0 15px 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
`;

export const StatePerformanceAnalytics = ({ data }) => {
  const [viewMode, setViewMode] = useState('completion');
  
  if (!data) {
    return (
      <Section>
        <ResponsiveTitle>State Performance Analytics</ResponsiveTitle>
        <p>No state performance data available</p>
      </Section>
    );
  }

  // Extract all state data
  const states = [
    {
      key: 'intro',
      name: 'Intro Completed',
      completion: data.state_intro_completed_percentage || 0,
      medianTime: data.state_intro_median_time_played,
      medianBlocks: data.state_intro_median_blocks_advanced,
      color: '#00BCD4'
    },
    {
      key: 'setup',
      name: 'Setup Completed',
      completion: data.state_setup_completed_percentage || 0,
      medianTime: data.state_setup_median_time_played,
      medianBlocks: data.state_setup_median_blocks_advanced,
      color: '#4CAF50'
    },
    {
      key: 'firstturn',
      name: 'First Turn',
      completion: data.state_firstturn_completed_percentage || 0,
      medianTime: data.state_firstturn_median_time_played,
      medianBlocks: data.state_firstturn_median_blocks_advanced,
      color: '#F44336'
    },
    {
      key: 'teaching',
      name: 'Teaching',
      completion: data.state_teaching_completed_percentage || 0,
      medianTime: data.state_teaching_median_time_played,
      medianBlocks: data.state_teaching_median_blocks_advanced,
      color: '#2196F3'
    },
    {
      key: 'gameplay',
      name: 'Gameplay',
      completion: data.state_gameplay_completed_percentage || 0,
      medianTime: data.state_gameplay_median_time_played,
      medianBlocks: data.state_gameplay_median_blocks_advanced,
      color: '#FF9800'
    },
    {
      key: 'tutorial',
      name: 'Tutorial Completed',
      completion: data.state_tutorial_completed_percentage || 0,
      medianTime: data.state_tutorial_median_time_played,
      medianBlocks: data.state_tutorial_median_blocks_advanced,
      color: '#9C27B0'
    }
  ];

  // Filter out states with no data using centralized validation
  const validStates = states.filter(state => isValidStateData(state.completion, state.medianTime, state.medianBlocks));

  // Combination chart for completion with time overlay
  const completionChartData = {
    labels: validStates.map(state => state.name),
    datasets: [
      {
        type: 'bar',
        label: 'Completion Rate (%)',
        data: validStates.map(state => state.completion),
        backgroundColor: validStates.map(state => state.color + '80'),
        borderColor: validStates.map(state => state.color),
        borderWidth: 2,
        borderRadius: 4,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Median Time (minutes)',
        data: validStates.map(state => {
          if (!state.medianTime || state.medianTime === null) return 0;
          // Convert seconds to minutes for better readability
          return Math.round((state.medianTime / 60) * 10) / 10; // Round to 1 decimal place
        }),
        backgroundColor: 'rgba(33, 150, 243, 0.2)',
        borderColor: 'rgba(33, 150, 243, 1)',
        borderWidth: 3,
        tension: 0.4,
        fill: false,
        pointBackgroundColor: 'rgba(33, 150, 243, 1)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 6,
        yAxisID: 'y1',
      },
    ],
  };

  const timeChartData = {
    labels: validStates.map(state => state.name),
    datasets: [
      {
        label: 'Median Time (minutes)',
        data: validStates.map(state => {
          if (!state.medianTime || state.medianTime === null) return 0;
          // Convert seconds to minutes for better readability
          return Math.round((state.medianTime / 60) * 10) / 10; // Round to 1 decimal place
        }),
        backgroundColor: 'rgba(33, 150, 243, 0.8)',
        borderColor: 'rgba(33, 150, 243, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
      },
    ],
  };

  // Combination chart for blocks with completion overlay
  const blocksChartData = {
    labels: validStates.map(state => state.name),
    datasets: [
      {
        type: 'bar',
        label: 'Median Blocks Advanced',
        data: validStates.map(state => state.medianBlocks || 0),
        backgroundColor: 'rgba(244, 67, 54, 0.8)',
        borderColor: 'rgba(244, 67, 54, 1)',
        borderWidth: 2,
        borderRadius: 4,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Completion Rate (%)',
        data: validStates.map(state => state.completion),
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        borderColor: 'rgba(76, 175, 80, 1)',
        borderWidth: 3,
        tension: 0.4,
        fill: false,
        pointBackgroundColor: 'rgba(76, 175, 80, 1)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 6,
        yAxisID: 'y1',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.y;
            
            if (datasetLabel.includes('Time')) {
              // Value is in minutes, format it appropriately
              if (value >= 1) {
                return `${datasetLabel}: ${value.toFixed(1)} min`;
              }
              // Show seconds for values less than 1 minute
              const seconds = Math.round(value * 60);
              return `${datasetLabel}: ${seconds}s`;
            }
            
            if (datasetLabel.includes('Completion')) {
              return `${datasetLabel}: ${value.toFixed(1)}%`;
            }
            
            return `${datasetLabel}: ${value}`;
          }
        }
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#666',
        },
        title: {
          display: true,
          text: viewMode === 'completion' ? 'Completion Rate (%)' : 'Median Blocks Advanced',
          color: '#666',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: '#666',
          callback: function(value) {
            // Show time values as full integers
            return Math.round(value) + ' min';
          }
        },
        title: {
          display: true,
          text: viewMode === 'completion' ? 'Median Time (minutes)' : 'Completion Rate (%)',
          color: '#666',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
          maxRotation: 45,
        },
      },
    },
  };

  // Simple chart options for time-only view
  const simpleChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            // Value is already in minutes
            if (value >= 1) {
              return `Median Time: ${value.toFixed(1)} min`;
            }
            // Show seconds for values less than 1 minute
            const seconds = Math.round(value * 60);
            return `Median Time: ${seconds}s`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#666',
          callback: function(value) {
            return Math.round(value) + ' min';
          }
        },
        title: {
          display: true,
          text: 'Median Time (minutes)',
          color: '#666',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
          maxRotation: 45,
        },
      },
    },
  };

  const renderChart = () => {
    switch (viewMode) {
      case 'completion':
        // Bar chart for completion with line overlay for time
        return <Bar data={completionChartData} options={chartOptions} />;
      case 'time':
        // Pure line chart for time
        return <Line data={timeChartData} options={simpleChartOptions} />;
      case 'blocks':
        // Bar chart for blocks with line overlay for completion
        return <Bar data={blocksChartData} options={chartOptions} />;
      default:
        return <Bar data={completionChartData} options={chartOptions} />;
    }
  };

  const getChartTitle = () => {
    switch (viewMode) {
      case 'completion':
        return '📊 State Completion Rates with Time Overlay';
      case 'time':
        return '⏱️ Median Time per State';
      case 'blocks':
        return '🧩 Median Blocks Advanced with Completion Overlay';
      default:
        return '📊 State Completion Rates with Time Overlay';
    }
  };

  return (
    <Section>
      <ResponsiveTitle>State Performance Analytics</ResponsiveTitle>
      
      <ViewToggle>
        <ToggleButton 
          active={viewMode === 'completion'} 
          onClick={() => setViewMode('completion')}
        >
          📊 Completion Rates
        </ToggleButton>
        <ToggleButton 
          active={viewMode === 'time'} 
          onClick={() => setViewMode('time')}
        >
          ⏱️ Time Analysis
        </ToggleButton>
        <ToggleButton 
          active={viewMode === 'blocks'} 
          onClick={() => setViewMode('blocks')}
        >
          🧩 Blocks Analysis
        </ToggleButton>
      </ViewToggle>

      <ChartsContainer>
        <ChartWrapper>
          <ChartTitle>{getChartTitle()}</ChartTitle>
          <ChartContainer>
            {renderChart()}
          </ChartContainer>
        </ChartWrapper>
      </ChartsContainer>
    </Section>
  );
};

StatePerformanceAnalytics.propTypes = {
  data: PropTypes.shape({
    state_intro_completed_percentage: PropTypes.number,
    state_intro_median_time_played: PropTypes.number,
    state_intro_median_blocks_advanced: PropTypes.number,
    state_setup_completed_percentage: PropTypes.number,
    state_setup_median_time_played: PropTypes.number,
    state_setup_median_blocks_advanced: PropTypes.number,
    state_tutorial_completed_percentage: PropTypes.number,
    state_tutorial_median_time_played: PropTypes.number,
    state_tutorial_median_blocks_advanced: PropTypes.number,
    state_firstturn_completed_percentage: PropTypes.number,
    state_firstturn_median_time_played: PropTypes.number,
    state_firstturn_median_blocks_advanced: PropTypes.number,
    state_teaching_completed_percentage: PropTypes.number,
    state_teaching_median_time_played: PropTypes.number,
    state_teaching_median_blocks_advanced: PropTypes.number,
    state_gameplay_completed_percentage: PropTypes.number,
    state_gameplay_median_time_played: PropTypes.number,
    state_gameplay_median_blocks_advanced: PropTypes.number
  })
};

StatePerformanceAnalytics.displayName = 'StatePerformanceAnalytics';
